import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, Dimensions } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getUsers, deleteUser, updateUser } from '../../../api/User';
import { msgStr } from '../../../common/constants/Message';
import { TextMediumSize } from '../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';
import BasicLayout from '../../../common/components/CustomLayout/BasicLayout';
import Pagination from '../../../common/components/ui/Pagination';
import { BOHTable, BOHTHead, BOHTBody, BOHTR, BOHTH, BOHTD, BOHTDIconBox } from '../../../common/components/bohtable';
import { BOHToolbar, BOHButton, BOHTlbrSearchInput } from '../../../common/components/bohtoolbar';

import { UserStyles as styles } from './styles/UserStyle';
import AddUserModal from './AddUserModal';

const USER_TYPES = {
  1: { label: 'Client', color: '#FF9800' },
  2: { label: 'Delivery Driver', color: '#009688' },
  3: { label: 'System Admin', color: '#4CAF50' },
  4: { label: 'Valet', color: '#9C27B0' },
};

const Users = ({ navigation, openInventory }) => {
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateUserTrigger, setUpdateUserTrigger] = useState(true);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(100);

  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const onSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const openAddUserModal = () => {
    setAddModalVisible(true);
    setSelectedUser(null);
  };

  const closeAddUserModal = () => {
    setAddModalVisible(false);
    setSelectedUser(null);
  };

  const editUser = (item) => {
    setSelectedUser(item);
    setAddModalVisible(true);
  };

  useEffect(() => {
    fetchUsers();
  }, [updateUserTrigger, currentPage, limit, searchQuery, sortField, sortOrder]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchInput]);

  const removeUser = (id) => {
    showConfirm(msgStr('deleteConfirmStr'), () => {
      deleteUser(id, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            setUpdateUserTrigger(true);
            showAlert('success', jsonRes.message || 'User deleted successfully');
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };


  const toggleVerification = (user) => {
    const newStatus = user.isVerified ? 0 : 1;
    const payload = { isVerified: newStatus };
    
    updateUser(user.id, payload, (jsonRes, status) => {
      if (status === 200) {
        setUpdateUserTrigger(true);
        showAlert('success', jsonRes.message || 'User verification updated');
      } else {
        showAlert('error', jsonRes.error || msgStr('unknownError'));
      }
    });
  };

  const fetchUsers = () => {
    setLoading(true);
    const params: any = {
      page: currentPage,
      limit: limit,
      sort_by: sortField,
      sort_order: sortOrder,
    };

    if (searchQuery) {
      params.search = searchQuery;
    }

    getUsers(params, (jsonRes, status, error) => {
      setLoading(false);
      setUpdateUserTrigger(false);
      switch (status) {
        case 200:
          if (jsonRes.data) {
            setTableData(jsonRes.data);
            const totalPages = jsonRes.pagination?.totalPages || jsonRes.totalPages || 1;
            const totalRecords = jsonRes.pagination?.total || jsonRes.totalRecords || jsonRes.data.length;
            setTotalPages(totalPages);
            setTotalRecords(totalRecords);
          } else if (Array.isArray(jsonRes)) {
            setTableData(jsonRes);
            setTotalPages(1);
            setTotalRecords(jsonRes.length);
          }
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const tableElement = useMemo(() => {
    const InitialWidths = [200, 250, 100, 150, 150, 150, 180, 100, 80, 80];

    return (
      <>
        <BOHTable isLoading={loading}>
          <BOHTHead>
            <BOHTR>
              <BOHTH
                width={InitialWidths[0]}
                onClick={() => onSort('name')}
                isSorted={sortField === 'name'}
                direction={sortField === 'name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : null}
              >
                Name
              </BOHTH>
              <BOHTH
                width={InitialWidths[1]}
                onClick={() => onSort('email')}
                isSorted={sortField === 'email'}
                direction={sortField === 'email' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : null}
              >
                Email
              </BOHTH>
              <BOHTH
                width={InitialWidths[2]}
                style={{ textAlign: 'center' }}
                onClick={() => onSort('is_driver')}
                isSorted={sortField === 'is_driver'}
                direction={sortField === 'is_driver' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : null}
              >
                Is Driver
              </BOHTH>
              <BOHTH
                width={InitialWidths[3]}
                onClick={() => onSort('createdAt')}
                isSorted={sortField === 'createdAt'}
                direction={sortField === 'createdAt' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : null}
              >
                Created At
              </BOHTH>
              <BOHTH width={InitialWidths[4]}>User Type</BOHTH>
              <BOHTH width={InitialWidths[5]}>Assign Brands</BOHTH>
              <BOHTH width={InitialWidths[6]}>Restricted Permissions</BOHTH>
              <BOHTH
                width={InitialWidths[7]}
                style={{ textAlign: 'center' }}
                onClick={() => onSort('isVerified')}
                isSorted={sortField === 'isVerified'}
                direction={sortField === 'isVerified' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : null}
              >
                Verified
              </BOHTH>
              <BOHTH width={InitialWidths[8]} style={{ textAlign: 'center' }}>Edit</BOHTH>
              <BOHTH width={InitialWidths[9]} style={{ textAlign: 'center' }}>Del</BOHTH>
            </BOHTR>
          </BOHTHead>
          <BOHTBody>
            {tableData.length > 0 ? (
              tableData.map((item, index) => (
                <BOHTR key={index}>
                  <BOHTD width={InitialWidths[0]}>{item.name}</BOHTD>
                  <BOHTD width={InitialWidths[1]}>{item.email}</BOHTD>
                  <BOHTD width={InitialWidths[2]} textAlign="center">
                    {item.is_driver ? 'Yes' : 'No'}
                  </BOHTD>
                  <BOHTD width={InitialWidths[3]}>
                    {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}
                  </BOHTD>
                  <BOHTD width={InitialWidths[4]}>
                    {item.userType && USER_TYPES[item.userType] ? (
                      <View style={{
                        backgroundColor: USER_TYPES[item.userType].color,
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 12,
                        alignSelf: 'flex-start'
                      }}>
                        <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                          {USER_TYPES[item.userType].label}
                        </Text>
                      </View>
                    ) : (
                      <Text>-</Text>
                    )}
                  </BOHTD>
                  <BOHTD width={InitialWidths[5]}>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                      {item.assignedBrands && Array.isArray(item.assignedBrands) && item.assignedBrands.length > 0 ? (
                        item.assignedBrands.map((brand, bIdx) => (
                          <View key={bIdx} style={{
                            backgroundColor: '#4CAF50',
                            paddingHorizontal: 8,
                            paddingVertical: 2,
                            borderRadius: 12,
                          }}>
                            <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>
                              {brand.name || brand.id}
                            </Text>
                          </View>
                        ))
                      ) : (
                        <View style={{
                          backgroundColor: '#9E9E9E',
                          paddingHorizontal: 8,
                          paddingVertical: 2,
                          borderRadius: 12,
                        }}>
                          <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>All</Text>
                        </View>
                      )}
                    </View>
                  </BOHTD>
                  <BOHTD width={InitialWidths[6]}>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                      {item.restrictedPermissions && Array.isArray(item.restrictedPermissions) && item.restrictedPermissions.length > 0 ? (
                        item.restrictedPermissions.map((permission, pIdx) => (
                          <View key={pIdx} style={{
                            backgroundColor: '#4CAF50',
                            paddingHorizontal: 8,
                            paddingVertical: 2,
                            borderRadius: 12,
                          }}>
                            <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>
                              {permission.name || permission.id}
                            </Text>
                          </View>
                        ))
                      ) : (
                        <View style={{
                          backgroundColor: '#9E9E9E',
                          paddingHorizontal: 8,
                          paddingVertical: 2,
                          borderRadius: 12,
                        }}>
                          <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>None</Text>
                        </View>
                      )}
                    </View>
                  </BOHTD>
                  <BOHTD width={InitialWidths[7]} textAlign="center">
                    <TouchableOpacity onPress={() => toggleVerification(item)}>
                      <FontAwesome5 
                        name={item.isVerified ? 'check-circle' : 'times-circle'} 
                        size={TextMediumSize} 
                        color={item.isVerified ? '#2E7D32' : '#D32F2F'} 
                      />
                    </TouchableOpacity>
                  </BOHTD>
                  <BOHTDIconBox width={InitialWidths[8]}>
                    <TouchableOpacity onPress={() => editUser(item)}>
                      <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
                    </TouchableOpacity>
                  </BOHTDIconBox>
                  <BOHTDIconBox width={InitialWidths[9]}>
                    <TouchableOpacity onPress={() => removeUser(item.id)}>
                      <FontAwesome5 size={TextMediumSize} name="times" color="black" />
                    </TouchableOpacity>
                  </BOHTDIconBox>
                </BOHTR>
              ))
            ) : !loading && (
              <View style={{ padding: 20, alignItems: 'center' }}>
                <Text style={{ color: '#999' }}>No users found</Text>
              </View>
            )}
          </BOHTBody>
        </BOHTable>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          disabled={loading}
          totalRecords={totalRecords}
          limit={limit}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setCurrentPage(1);
          }}
        />
      </>
    );
  }, [tableData, loading, currentPage, totalPages, sortField, sortOrder, limit]);

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        openInventory(null);
      }}
      screenName={'User Management'}
    >
      <View style={{ flex: 1, padding: 20 }}>
        <BOHToolbar>
          <BOHTlbrSearchInput
            label="Search"
            width={250}
            value={searchInput}
            onChangeText={(val) => setSearchInput(val)}
            onSubmitEditing={() => {
              if (searchInput !== searchQuery) {
                setSearchQuery(searchInput);
                setCurrentPage(1);
              }
              setUpdateUserTrigger(true);
            }}
          />
          <View style={{ flex: 1 }} />
          <BOHButton
            label="Add User"
            onPress={openAddUserModal}
          />
        </BOHToolbar>

        {tableElement}

        <AddUserModal
          isModalVisible={isAddModalVisible}
          user={selectedUser}
          setUpdateUserTrigger={setUpdateUserTrigger}
          closeModal={closeAddUserModal}
        />
      </View>
    </BasicLayout>
  );
};

export default Users;
